import { hasAccess } from "$root/components/AuthorizationGuard";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { getCurrentUser } from "$root/functional-areas/user";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import type * as smartWorkspaces from "@eu.mdotm.api/smart-workspaces";
import type { UserInstrumentClassificationDto } from "@eu.mdotm.api/sphere";
import { readAtom } from "@mdotm/shadcn/legacy/reactivity";
import {
	columnIdToConcept,
	INSTRUMENT_IDENTITY_COL_IDS,
	RISK_MODEL_COL_ID,
	RISK_MODEL_CONCEPT,
	withUuid,
} from "./concepts";
import { buildSortCriteria, orderedVisibleColIds, readPersistedColumns } from "./sorting";

/** Default column order (all columns visible) mirroring the universe instrument table. */
function defaultUniverseColumnOrder(classifications: UserInstrumentClassificationDto[]): string[] {
	return [
		...INSTRUMENT_IDENTITY_COL_IDS,
		...classifications.map((c) => c.classificationUuid).filter((uuid): uuid is string => uuid != null),
	];
}

/**
 * Injects the export-only Risk Model column id right after the last instrument identity column in
 * `colIds` (or at the front when no identity column is present). Risk Model has no universe table
 * column, so it never appears in the persisted column state and must be added back explicitly.
 */
function withRiskModelColumn(colIds: string[]): string[] {
	const identityColIds = new Set<string>(INSTRUMENT_IDENTITY_COL_IDS);
	let lastIdentityIndex = -1;
	colIds.forEach((colId, index) => {
		if (identityColIds.has(colId)) {
			lastIdentityIndex = index;
		}
	});
	const result = [...colIds];
	result.splice(lastIdentityIndex + 1, 0, RISK_MODEL_COL_ID);
	return result;
}

/**
 * Builds the data request used to export a universe's instrument list through the smart-workspace
 * data provider (`exportWorkspaceData`). The exported concepts mirror the universe instrument table
 * the user sees: they are derived from the user's persisted column preferences (visibility + order)
 * stored under `tableV3-universe-composition-read`, falling back to the default all-columns layout
 * when no preference is stored. The export-only Risk Model column (which has no table column) is
 * projected right after the instrument identity columns, but only when the user has the
 * `RISK_MODEL_COLUMN` service — the same gate the instrument tables apply. `ACTION` is never
 * exported. `universeUuid` is parametrized. Sorting mirrors the table's persisted sort criteria,
 * resolved against the projected concepts.
 */
export function buildUniverseInstrumentExportConfig(universeUuid: string): smartWorkspaces.DataRequestDTO {
	const classifications = readAtom(userClassifications$);
	const persistedColumns = readPersistedColumns(persistedColStateKeys["tableV3-universe-composition-read"]);
	const visibleColIds = persistedColumns
		? orderedVisibleColIds(persistedColumns)
		: defaultUniverseColumnOrder(classifications);
	const columnOrder = hasAccess(getCurrentUser(), { requiredService: "RISK_MODEL_COLUMN" })
		? withRiskModelColumn(visibleColIds)
		: visibleColIds;

	// Resolve each visible column to its concept, keyed by colId so persisted sort criteria can be
	// mapped back to the concept UUIDs they reference.
	const conceptByColId = new Map<string, smartWorkspaces.ParameterizedConceptDTO>();
	for (const colId of columnOrder) {
		const concept =
			colId === RISK_MODEL_COL_ID ? withUuid(RISK_MODEL_CONCEPT) : columnIdToConcept(colId, classifications);
		if (concept) {
			conceptByColId.set(colId, concept);
		}
	}
	const concepts = [...conceptByColId.values()];
	const sorting = persistedColumns ? buildSortCriteria(persistedColumns, conceptByColId) : [];

	return {
		concepts,
		dataProvider: {
			dataProviderId: "userInstrumentCompositionByUniverse",
			parameters: { universeUuid: universeUuid as unknown as object },
		},
		projection: concepts.map((c) => c.uuid!),
		sorting: sorting.length > 0 ? sorting : undefined,
	};
}
